/**
 * Vehicle Classification Engine — Storage Benchmark Certification (Phase 1).
 *
 * A storage benchmark is only legally defensible when the benchmark market
 * matches the class of vehicle actually stored. This module resolves that
 * class *before* any benchmark is selected, using every evidence source that
 * is available on a claim:
 *
 *   invoice description · OCR raw text · VIN (WMI decode) · make · model ·
 *   body type · GVWR · recovery equipment · tow equipment · line items ·
 *   AI/OCR confidence
 *
 * No single field can carry a classification on its own above the
 * "corroborated" confidence threshold: `multiSource` is true only when two or
 * more independent sources agree. Consumers (storage-benchmark.ts) refuse to
 * emit a benchmark when the class is `unknown`.
 *
 * Deterministic and pure — no network, no AI. Fully unit-tested.
 */

export type VehicleClass =
  | "private_passenger"
  | "commercial"
  | "heavy_duty_commercial"
  | "semi_tractor"
  | "straight_truck"
  | "bus"
  | "rv"
  | "motorcycle"
  | "trailer"
  | "construction_equipment"
  | "agricultural_equipment"
  | "specialty"
  | "unknown";

export const VEHICLE_CLASS_LABELS: Record<VehicleClass, string> = {
  private_passenger: "Private Passenger Vehicle",
  commercial: "Commercial Vehicle",
  heavy_duty_commercial: "Heavy-Duty Commercial Vehicle",
  semi_tractor: "Semi Tractor",
  straight_truck: "Straight Truck",
  bus: "Bus",
  rv: "RV",
  motorcycle: "Motorcycle",
  trailer: "Trailer",
  construction_equipment: "Construction Equipment",
  agricultural_equipment: "Agricultural Equipment",
  specialty: "Specialty Vehicle",
  unknown: "Unclassified Vehicle",
};

export type ClassificationSource =
  | "vin"
  | "gvwr"
  | "make"
  | "model"
  | "body_type"
  | "vehicle_description"
  | "line_items"
  | "recovery_equipment"
  | "tow_equipment"
  | "ocr_text";

export interface ClassificationEvidence {
  source: ClassificationSource;
  signal: string;
  vehicleClass: VehicleClass;
  weight: number;
}

export interface VehicleClassification {
  vehicleClass: VehicleClass;
  label: string;
  /** 0-1. Blends signal weight, corroboration and upstream OCR confidence. */
  confidence: number;
  /** Every signal that fired, in descending weight order. */
  evidence: ClassificationEvidence[];
  /** Distinct evidence sources that supported the winning class. */
  sources: ClassificationSource[];
  /** True when ≥2 independent sources agree on the winning class. */
  multiSource: boolean;
}

export interface VehicleClassificationInput {
  vehicleDescription?: string | null;
  make?: string | null;
  model?: string | null;
  bodyType?: string | null;
  vin?: string | null;
  /** Gross vehicle weight rating in pounds, when extracted. */
  gvwrLbs?: number | null;
  lineItems?: Array<{ description?: string | null }>;
  /** Raw OCR text for the invoice (weakest source, used for corroboration). */
  rawText?: string | null;
  /** OCR / extraction confidence 0-1, used to temper the final score. */
  extractionConfidence?: number | null;
}

/* ───────────────────────── signal dictionaries ───────────────────────── */

type Rule = { re: RegExp; cls: VehicleClass; signal: string };

/** Make-level signals. Heavy truck marques are unambiguous. */
const MAKE_RULES: Rule[] = [
  { re: /\bkenworth\b/i, cls: "heavy_duty_commercial", signal: "Kenworth (Class 8 marque)" },
  { re: /\bpeterbilt\b/i, cls: "heavy_duty_commercial", signal: "Peterbilt (Class 8 marque)" },
  { re: /\bfreightliner\b/i, cls: "heavy_duty_commercial", signal: "Freightliner (Class 8 marque)" },
  { re: /\bmack\b/i, cls: "heavy_duty_commercial", signal: "Mack (Class 8 marque)" },
  { re: /\bwestern\s*star\b/i, cls: "heavy_duty_commercial", signal: "Western Star (Class 8 marque)" },
  { re: /\binternational\s*(prostar|lonestar|lt|hx|9\d{3})\b/i, cls: "heavy_duty_commercial", signal: "International heavy tractor" },
  { re: /\bvolvo\s*(vnl|vnr|vah)\b/i, cls: "heavy_duty_commercial", signal: "Volvo VN-series tractor" },
  { re: /\b(hino|isuzu\s*npr|fuso|ud\s*truck)\b/i, cls: "straight_truck", signal: "Medium-duty cabover marque" },
  { re: /\b(caterpillar|cat\s*\d{3}|komatsu|bobcat|deere\s*\d{3}|case\s*\d{3}|jcb)\b/i, cls: "construction_equipment", signal: "Construction equipment marque" },
  { re: /\b(john\s*deere|kubota|new\s*holland|massey\s*ferguson)\b/i, cls: "agricultural_equipment", signal: "Agricultural equipment marque" },
  { re: /\b(harley|ducati|kawasaki|yamaha|suzuki\s*gsx|triumph\s*motorcycle)\b/i, cls: "motorcycle", signal: "Motorcycle marque" },
  { re: /\b(winnebago|thor\s*motor|fleetwood\s*rv|newmar|tiffin)\b/i, cls: "rv", signal: "RV marque" },
  { re: /\b(blue\s*bird|thomas\s*built|prevost|mci\s*coach)\b/i, cls: "bus", signal: "Bus marque" },
  { re: /\b(toyota|honda|nissan|hyundai|kia|subaru|mazda|chevrolet\s*(malibu|impala|cruze)|ford\s*(focus|fusion|escape)|bmw|mercedes-benz\s*[cse]-class|audi|lexus)\b/i, cls: "private_passenger", signal: "Passenger marque/model" },
];

/** Body-type / description signals. */
const BODY_RULES: Rule[] = [
  { re: /\b(semi|tractor[- ]trailer|road\s*tractor|day\s*cab|sleeper\s*cab|18[- ]wheeler)\b/i, cls: "semi_tractor", signal: "Tractor / sleeper configuration" },
  { re: /\b(box\s*truck|straight\s*truck|dump\s*truck|cube\s*van|stake\s*bed|refuse\s*truck)\b/i, cls: "straight_truck", signal: "Straight-truck body" },
  { re: /\b(class\s*[678]|heavy[- ]duty|heavy\s*truck|tandem\s*axle|tri[- ]axle)\b/i, cls: "heavy_duty_commercial", signal: "Heavy-duty body/axle configuration" },
  { re: /\b(motor\s*coach|school\s*bus|transit\s*bus|\bbus\b)/i, cls: "bus", signal: "Bus body" },
  { re: /\b(motorhome|motor\s*home|rv\b|travel\s*trailer|fifth\s*wheel\s*rv|class\s*[abc]\s*motorhome)\b/i, cls: "rv", signal: "Recreational vehicle body" },
  { re: /\b(motorcycle|moped|scooter|atv\b)/i, cls: "motorcycle", signal: "Motorcycle body" },
  { re: /\b(trailer|lowboy|flatbed\s*trailer|tanker\s*trailer|reefer\s*trailer|ledwell\s*trailer)\b/i, cls: "trailer", signal: "Trailer unit" },
  { re: /\b(excavator|backhoe|skid\s*steer|bulldozer|loader|forklift|telehandler|crane\b)/i, cls: "construction_equipment", signal: "Construction equipment body" },
  { re: /\b(tractor\s*(?:combine|harvester)|combine|harvester|farm\s*tractor|implement)\b/i, cls: "agricultural_equipment", signal: "Agricultural equipment body" },
  { re: /\b(sedan|coupe|hatchback|minivan|suv\b|crossover|pickup\s*truck|passenger\s*(?:car|vehicle))\b/i, cls: "private_passenger", signal: "Passenger body style" },
  { re: /\b(ambulance|fire\s*apparatus|armored\s*(?:car|truck)|limousine|food\s*truck)\b/i, cls: "specialty", signal: "Specialty body" },
  { re: /\b(commercial\s*vehicle|cdl\b|dot\s*number|usdot|fleet\s*unit)\b/i, cls: "commercial", signal: "Commercial-use indicator" },
];

/**
 * Recovery / tow equipment signals. Equipment is a strong proxy for mass:
 * a rotator or heavy recovery van is never dispatched for a passenger car.
 */
const EQUIPMENT_RULES: Array<Rule & { source: ClassificationSource }> = [
  { re: /\brotator\b/i, cls: "heavy_duty_commercial", signal: "Rotator recovery unit", source: "recovery_equipment" },
  { re: /\b\d{2,3}\s*ton\b/i, cls: "heavy_duty_commercial", signal: "High-tonnage recovery unit", source: "recovery_equipment" },
  { re: /\bheavy\s*(?:duty\s*)?(?:recovery|wrecker|tow|storage)\b/i, cls: "heavy_duty_commercial", signal: "Heavy recovery / heavy tow service", source: "recovery_equipment" },
  { re: /\brecovery\s*van\b/i, cls: "heavy_duty_commercial", signal: "Heavy recovery support van", source: "recovery_equipment" },
  { re: /\bcertified\s*rigger\b/i, cls: "heavy_duty_commercial", signal: "Certified rigger crew (heavy recovery)", source: "recovery_equipment" },
  { re: /\b(landoll|lowboy|detach(?:able)?\s*(?:goose)?neck)\b/i, cls: "heavy_duty_commercial", signal: "Heavy-haul transport equipment", source: "tow_equipment" },
  { re: /\b(road\s*tractor|tractor\s*unit)\b/i, cls: "semi_tractor", signal: "Road tractor deployed", source: "tow_equipment" },
  { re: /\b(rollback|flatbed)\b/i, cls: "commercial", signal: "Rollback / flatbed carrier", source: "tow_equipment" },
  { re: /\b(light\s*duty\s*tow|wheel\s*lift)\b/i, cls: "private_passenger", signal: "Light-duty tow equipment", source: "tow_equipment" },
  { re: /\bmotorcycle\s*(?:tow|transport|cradle)\b/i, cls: "motorcycle", signal: "Motorcycle transport equipment", source: "tow_equipment" },
];

/** VIN World Manufacturer Identifier prefixes for non-passenger classes. */
const VIN_WMI: Array<{ prefix: string; cls: VehicleClass; signal: string }> = [
  { prefix: "1XK", cls: "heavy_duty_commercial", signal: "VIN WMI 1XK — Kenworth heavy truck" },
  { prefix: "2XK", cls: "heavy_duty_commercial", signal: "VIN WMI 2XK — Kenworth heavy truck" },
  { prefix: "1NP", cls: "heavy_duty_commercial", signal: "VIN WMI 1NP — Peterbilt heavy truck" },
  { prefix: "1FU", cls: "heavy_duty_commercial", signal: "VIN WMI 1FU — Freightliner heavy truck" },
  { prefix: "3AK", cls: "heavy_duty_commercial", signal: "VIN WMI 3AK — Freightliner heavy truck" },
  { prefix: "1M1", cls: "heavy_duty_commercial", signal: "VIN WMI 1M1 — Mack heavy truck" },
  { prefix: "4V4", cls: "heavy_duty_commercial", signal: "VIN WMI 4V4 — Volvo heavy truck" },
  { prefix: "5PV", cls: "straight_truck", signal: "VIN WMI 5PV — Hino medium-duty truck" },
  { prefix: "1BA", cls: "bus", signal: "VIN WMI 1BA — Blue Bird bus" },
  { prefix: "4UZ", cls: "bus", signal: "VIN WMI 4UZ — Freightliner bus/RV chassis" },
  { prefix: "1HG", cls: "private_passenger", signal: "VIN WMI 1HG — Honda passenger car" },
  { prefix: "JHM", cls: "private_passenger", signal: "VIN WMI JHM — Honda passenger car" },
  { prefix: "1G1", cls: "private_passenger", signal: "VIN WMI 1G1 — Chevrolet passenger car" },
  { prefix: "5YJ", cls: "private_passenger", signal: "VIN WMI 5YJ — Tesla passenger car" },
  { prefix: "1HD", cls: "motorcycle", signal: "VIN WMI 1HD — Harley-Davidson motorcycle" },
];

/* ───────────────────────────── engine ───────────────────────────── */

const SOURCE_WEIGHT: Record<ClassificationSource, number> = {
  vin: 5,
  gvwr: 4.5,
  make: 4,
  model: 3,
  body_type: 3.5,
  vehicle_description: 3,
  recovery_equipment: 3,
  tow_equipment: 2,
  line_items: 2,
  ocr_text: 1,
};

function push(
  out: ClassificationEvidence[],
  source: ClassificationSource,
  cls: VehicleClass,
  signal: string,
) {
  if (cls === "unknown") return;
  if (out.some((e) => e.source === source && e.signal === signal)) return;
  out.push({ source, signal, vehicleClass: cls, weight: SOURCE_WEIGHT[source] });
}

function scanText(
  out: ClassificationEvidence[],
  source: ClassificationSource,
  text: string | null | undefined,
  rules: Rule[],
) {
  if (!text) return;
  for (const rule of rules) if (rule.re.test(text)) push(out, source, rule.cls, rule.signal);
}

function scanEquipment(out: ClassificationEvidence[], text: string | null | undefined) {
  if (!text) return;
  for (const rule of EQUIPMENT_RULES)
    if (rule.re.test(text)) push(out, rule.source, rule.cls, rule.signal);
}

/** Weight-rating buckets per FHWA vehicle classes. */
function classFromGvwr(lbs: number): { cls: VehicleClass; signal: string } | null {
  if (!Number.isFinite(lbs) || lbs <= 0) return null;
  if (lbs <= 8500) return { cls: "private_passenger", signal: `GVWR ${lbs} lbs (Class 1-2a)` };
  if (lbs <= 14000) return { cls: "commercial", signal: `GVWR ${lbs} lbs (Class 2b-3)` };
  if (lbs <= 26000) return { cls: "straight_truck", signal: `GVWR ${lbs} lbs (Class 4-6)` };
  return { cls: "heavy_duty_commercial", signal: `GVWR ${lbs} lbs (Class 7-8)` };
}


export function classifyVehicle(input: VehicleClassificationInput): VehicleClassification {
  const evidence: ClassificationEvidence[] = [];

  const vin = (input.vin ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  if (vin.length >= 11) {
    const hit = VIN_WMI.find((w) => vin.startsWith(w.prefix));
    if (hit) push(evidence, "vin", hit.cls, hit.signal);
  }

  if (typeof input.gvwrLbs === "number") {
    const g = classFromGvwr(input.gvwrLbs);
    if (g) push(evidence, "gvwr", g.cls, g.signal);
  }

  scanText(evidence, "make", input.make, MAKE_RULES);
  scanText(evidence, "model", input.model, [...MAKE_RULES, ...BODY_RULES]);
  scanText(evidence, "body_type", input.bodyType, BODY_RULES);
  scanText(evidence, "vehicle_description", input.vehicleDescription, [...MAKE_RULES, ...BODY_RULES]);
  scanEquipment(evidence, input.vehicleDescription);

  const lineText = (input.lineItems ?? [])
    .map((li) => li?.description ?? "")
    .filter(Boolean)
    .join("\n");
  scanText(evidence, "line_items", lineText, [...MAKE_RULES, ...BODY_RULES]);
  scanEquipment(evidence, lineText);

  const raw = input.rawText ?? "";
  // "Vehicle: kenworth" style OCR fields are stronger than loose body text.
  const vehicleField = /vehicle\s*[:#-]\s*([^\n\r]{2,80})/i.exec(raw)?.[1] ?? null;
  scanText(evidence, "vehicle_description", vehicleField, [...MAKE_RULES, ...BODY_RULES]);
  scanText(evidence, "ocr_text", raw, [...MAKE_RULES, ...BODY_RULES]);
  scanEquipment(evidence, raw);

  // Score by class, counting each distinct source once at its full weight.
  const perClass = new Map<VehicleClass, { score: number; sources: Set<ClassificationSource> }>();
  for (const e of evidence) {
    const bucket = perClass.get(e.vehicleClass) ?? { score: 0, sources: new Set() };
    if (!bucket.sources.has(e.source)) {
      bucket.score += e.weight;
      bucket.sources.add(e.source);
    } else {
      bucket.score += e.weight * 0.25; // corroborating signal from the same source
    }
    perClass.set(e.vehicleClass, bucket);
  }

  // Heavy family consolidation: a semi tractor is also a heavy-duty commercial
  // vehicle, so their evidence reinforces rather than competes. They share one
  // storage market, so the combined score is attributed to the better-supported
  // of the two and the other is dropped.
  const semi = perClass.get("semi_tractor");
  const heavy = perClass.get("heavy_duty_commercial");
  if (semi && heavy) {
    const combined = semi.score + heavy.score;
    const sources = new Set([...semi.sources, ...heavy.sources]);
    const keep: VehicleClass = semi.score > heavy.score ? "semi_tractor" : "heavy_duty_commercial";
    const drop: VehicleClass = keep === "semi_tractor" ? "heavy_duty_commercial" : "semi_tractor";
    perClass.set(keep, { score: combined, sources });
    perClass.delete(drop);
  }


  const ranked = [...perClass.entries()].sort((a, b) => b[1].score - a[1].score);
  const winner = ranked[0];

  if (!winner || winner[1].score <= 0) {
    return {
      vehicleClass: "unknown",
      label: VEHICLE_CLASS_LABELS.unknown,
      confidence: 0,
      evidence: [],
      sources: [],
      multiSource: false,
    };
  }

  const [cls, bucket] = winner;
  const runnerUp = ranked[1]?.[1].score ?? 0;
  const sources = [...bucket.sources];
  const multiSource = sources.length >= 2;

  // Confidence: saturating score term, separation from the runner-up, and the
  // upstream extraction confidence. Single-source classifications are capped.
  const scoreTerm = Math.min(1, bucket.score / 8);
  const separation = bucket.score > 0 ? Math.min(1, (bucket.score - runnerUp) / bucket.score) : 0;
  const extraction = typeof input.extractionConfidence === "number" ? Math.max(0.4, Math.min(1, input.extractionConfidence)) : 0.85;
  let confidence = (scoreTerm * 0.55 + separation * 0.25 + 0.2) * extraction;
  if (!multiSource) confidence = Math.min(confidence, 0.6);
  confidence = Math.max(0, Math.min(1, Math.round(confidence * 100) / 100));

  return {
    vehicleClass: cls,
    label: VEHICLE_CLASS_LABELS[cls],
    confidence,
    evidence: evidence.sort((a, b) => b.weight - a.weight),
    sources,
    multiSource,
  };
}

/** Evidence lines that supported the winning class, for report transparency. */
export function classificationRationale(c: VehicleClassification, max = 4): string[] {
  return c.evidence
    .filter((e) => e.vehicleClass === c.vehicleClass)
    .slice(0, max)
    .map((e) => e.signal);
}
